/**
 * MarketingCampaignDetailPage — Phase 32.9.
 *
 * Shows all specialist outputs for one campaign (landing page, blog,
 * SEO, pricing — all bundled in the campaign_output JSON blob).
 * Gives the founder: Approve, Reject, Re-run, and per-content View/Edit.
 * All mutations are founder-signed.
 */

import { useState } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { apiFetch, apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CampaignSummary {
  id: string;
  product_id: string;
  status: string;
  omega_log_seq: number;
  created_at: number;
  executed_at: number | null;
  slug: string | null;
  title_tag: string | null;
}

interface ContentSummary {
  id: string;
  campaign_id: string;
  content_type: string;
  body: string;
  brand_voice_version: string;
  llm_response_hash: string | null;
  created_at: number;
}

interface CampaignDetailResponse {
  campaign: CampaignSummary;
  content: ContentSummary[];
}

// ── API ───────────────────────────────────────────────────────────────────────

function fetchDetail(id: string): Promise<CampaignDetailResponse> {
  return apiFetch<CampaignDetailResponse>(`/admin/marketing/campaigns/${id}`);
}

function sortKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(sortKeys);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return obj;
}

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function callAction(
  campaignId: string,
  action: 'approve' | 'reject' | 'rerun',
  extra?: Record<string, unknown>,
): Promise<void> {
  const body: Record<string, unknown> = { nonce: crypto.randomUUID(), ...extra };
  const canonical = JSON.stringify(sortKeys(body));
  const hashHex = await sha256hex(canonical);
  const sig = await signHashWithFounderKey(hashHex);
  const token = await getAccessToken();
  const resp = await fetch(
    `${apiBaseUrl()}/admin/marketing/campaigns/${campaignId}/${action}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Founder-Signature': sig,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: canonical,
    },
  );
  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${resp.status}`);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  pending:   '#7dd3fc',
  running:   '#a78bfa',
  approved:  '#6ee7b7',
  published: '#34d399',
  rejected:  '#f87171',
  failed:    '#f87171',
};

function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function parseCampaignOutput(body: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(body) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

// ── 4-Specialist panel layout ─────────────────────────────────────────────────

interface SpecialistPanelProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  contentId?: string | undefined;
}

function SpecialistPanel({ title, icon, children, contentId }: SpecialistPanelProps) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        <span style={{ fontSize: '13pt' }}>{icon}</span>
        <span style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--omega-ink)' }}>
          {title}
        </span>
        {contentId && (
          <Link
            to={`/omega/marketing/content/${contentId}`}
            style={{
              marginLeft: 'auto',
              fontSize: '7.5pt',
              color: '#7dd3fc',
              textDecoration: 'none',
              border: '1px solid rgba(125,211,252,0.3)',
              borderRadius: 3,
              padding: '1px 7px',
              letterSpacing: '0.04em',
            }}
          >
            View / Edit
          </Link>
        )}
      </div>
      <div style={{ padding: '12px 14px', fontSize: '9pt' }}>{children}</div>
    </div>
  );
}

function EmptyPanel() {
  return (
    <div style={{ color: 'var(--omega-ink-subtle)', fontStyle: 'italic', fontSize: '8.5pt' }}>
      Not yet generated.
    </div>
  );
}

function KeywordList({ keywords }: { keywords: unknown }) {
  if (!Array.isArray(keywords) || keywords.length === 0) return <EmptyPanel />;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {(keywords as unknown[]).map((k, i) => (
        <span
          key={i}
          style={{
            background: 'rgba(125,211,252,0.1)',
            border: '1px solid rgba(125,211,252,0.25)',
            borderRadius: 3,
            color: '#7dd3fc',
            fontSize: '8pt',
            padding: '2px 7px',
          }}
        >
          {String(k)}
        </span>
      ))}
    </div>
  );
}

function CampaignPanels({ content }: { content: ContentSummary[] }) {
  // Find the campaign_output blob. All 4 specialist results live there.
  const outputRow = content.find(c => c.content_type === 'campaign_output');
  const parsed = outputRow ? parseCampaignOutput(outputRow.body) : null;
  const cid = outputRow?.id;

  if (!outputRow && content.length === 0) {
    return (
      <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '20px 0' }}>
        No content generated yet.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      {/* 1 — Landing Page */}
      <SpecialistPanel title="Landing Page" icon="🖥" contentId={cid}>
        {parsed?.hero_h1 ? (
          <div>
            <div style={{ fontWeight: 700, fontSize: '11pt', marginBottom: 6 }}>{String(parsed.hero_h1)}</div>
            {!!parsed.hero_subtitle && (
              <div style={{ color: 'var(--omega-ink-subtle)', marginBottom: 8 }}>{String(parsed.hero_subtitle)}</div>
            )}
            {!!parsed.landing_page_md && (
              <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>
                {String(parsed.landing_page_md).slice(0, 180)}…
              </div>
            )}
          </div>
        ) : (
          <EmptyPanel />
        )}
      </SpecialistPanel>

      {/* 2 — Blog Post */}
      <SpecialistPanel title="Blog Post" icon="✍" contentId={cid}>
        {!!parsed?.blog_post_md ? (
          <div>
            <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink)', lineHeight: 1.6 }}>
              {String(parsed.blog_post_md).slice(0, 220)}…
            </div>
          </div>
        ) : (
          <EmptyPanel />
        )}
      </SpecialistPanel>

      {/* 3 — SEO */}
      <SpecialistPanel title="SEO" icon="🔍" contentId={cid}>
        {!!parsed?.meta_description ? (
          <div>
            <div style={{ color: 'var(--omega-ink)', marginBottom: 10 }}>{String(parsed.meta_description)}</div>
            <KeywordList keywords={parsed.keywords} />
          </div>
        ) : (
          <EmptyPanel />
        )}
      </SpecialistPanel>

      {/* 4 — Pricing */}
      <SpecialistPanel title="Pricing" icon="💰" contentId={cid}>
        {parsed?.price_usd_cents !== undefined ? (
          <div>
            <div style={{ fontSize: '18pt', fontWeight: 700, color: '#6ee7b7', marginBottom: 4 }}>
              ${((parsed.price_usd_cents as number) / 100).toFixed(2)}
              <span style={{ fontSize: '10pt', color: 'var(--omega-ink-subtle)', marginLeft: 4 }}>/mo</span>
            </div>
            {parsed.tier_name != null && (
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
                Tier: {String(parsed.tier_name)}
              </div>
            )}
          </div>
        ) : (
          <EmptyPanel />
        )}
      </SpecialistPanel>

      {/* Extra content rows (non-campaign_output) */}
      {content
        .filter(c => c.content_type !== 'campaign_output')
        .map(c => (
          <SpecialistPanel key={c.id} title={c.content_type} icon="📄" contentId={c.id}>
            <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink)', lineHeight: 1.5 }}>
              {c.body.slice(0, 200)}…
            </div>
          </SpecialistPanel>
        ))}
    </div>
  );
}

// ── Action bar ────────────────────────────────────────────────────────────────

function ActionBar({
  campaign,
  onAction,
  busy,
  error,
}: {
  campaign: CampaignSummary;
  onAction: (action: 'approve' | 'reject' | 'rerun', extra?: Record<string, unknown>) => void;
  busy: boolean;
  error: string | null;
}) {
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);

  const isTerminal = campaign.status === 'approved' || campaign.status === 'rejected' || campaign.status === 'published';

  const btnBase: React.CSSProperties = {
    fontFamily: 'var(--omega-mono)',
    fontSize: '9pt',
    fontWeight: 600,
    border: 'none',
    borderRadius: 4,
    padding: '7px 16px',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'var(--omega-bg, #0a0d12)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '12px 0 8px',
        marginTop: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {error && <div style={{ color: '#f87171', fontSize: '8.5pt' }}>{error}</div>}

      {/* Reject input */}
      {showReject && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            autoFocus
            placeholder="Rejection reason (optional)…"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 4,
              color: 'var(--omega-ink)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '9pt',
              padding: '5px 10px',
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') { setShowReject(false); setRejectReason(''); }
              if (e.key === 'Enter') { onAction('reject', { reason: rejectReason }); setShowReject(false); }
            }}
          />
          <button
            onClick={() => { onAction('reject', { reason: rejectReason }); setShowReject(false); }}
            disabled={busy}
            style={{ ...btnBase, background: '#f87171', color: '#000' }}
          >
            Confirm Reject
          </button>
        </div>
      )}

      {/* Re-run confirm */}
      {showRerunConfirm && (
        <div
          style={{
            background: 'rgba(251,191,36,0.08)',
            border: '1px solid rgba(251,191,36,0.25)',
            borderRadius: 4,
            padding: '8px 12px',
            fontSize: '8.5pt',
            color: '#fbbf24',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span>Re-run will clear existing content and queue a new brain execution. Confirm?</span>
          <button
            onClick={() => { onAction('rerun'); setShowRerunConfirm(false); }}
            disabled={busy}
            style={{ ...btnBase, padding: '4px 12px', background: '#fbbf24', color: '#000', fontSize: '8pt' }}
          >
            Yes, Re-run
          </button>
          <button
            onClick={() => setShowRerunConfirm(false)}
            style={{ ...btnBase, padding: '4px 10px', background: 'rgba(255,255,255,0.07)', color: 'var(--omega-ink)', fontSize: '8pt' }}
          >
            Cancel
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {!isTerminal && (
          <>
            <button
              onClick={() => onAction('approve')}
              disabled={busy}
              style={{ ...btnBase, background: '#6ee7b7', color: '#000' }}
            >
              {busy ? 'Signing…' : 'Approve'}
            </button>
            <button
              onClick={() => setShowReject(s => !s)}
              disabled={busy}
              style={{ ...btnBase, background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
            >
              Reject
            </button>
          </>
        )}

        {/* Re-run always available (unless already running) */}
        {campaign.status !== 'running' && (
          <button
            onClick={() => setShowRerunConfirm(true)}
            disabled={busy}
            style={{ ...btnBase, background: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}
          >
            Re-run Brain
          </button>
        )}

        {isTerminal && (
          <div
            style={{
              padding: '6px 14px',
              borderRadius: 4,
              background: campaign.status === 'approved' ? 'rgba(110,231,183,0.08)' : 'rgba(248,113,113,0.08)',
              color: campaign.status === 'approved' ? '#6ee7b7' : '#f87171',
              border: `1px solid ${campaign.status === 'approved' ? 'rgba(110,231,183,0.25)' : 'rgba(248,113,113,0.25)'}`,
              fontSize: '8.5pt',
              letterSpacing: '0.04em',
            }}
          >
            {campaign.status.toUpperCase()}
            {campaign.executed_at ? ` — ${fmtTime(campaign.executed_at)}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MarketingCampaignDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['marketing', 'campaign', id],
    queryFn: () => fetchDetail(id),
    refetchInterval: 15_000,
    enabled: !!id,
  });

  const actionMutation = useMutation({
    mutationFn: ({ action, extra }: { action: 'approve' | 'reject' | 'rerun'; extra?: Record<string, unknown> }) =>
      callAction(id, action, extra),
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['marketing', 'campaign', id] });
      void qc.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const campaign = data?.campaign;
  const content = data?.content ?? [];

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}
    >
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Marketing', to: '/omega/marketing' },
          { label: 'Campaigns', to: '/omega/marketing/campaigns' },
          { label: campaign?.title_tag ?? id.slice(0, 8) },
        ]}
      />

      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', marginTop: 16 }}>Loading…</div>
      )}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt', marginTop: 16 }}>Failed to load campaign.</div>
      )}

      {campaign && (
        <>
          {/* Header */}
          <div style={{ marginTop: 8, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: '13pt', fontWeight: 700, letterSpacing: '0.04em' }}>
                {campaign.title_tag ?? campaign.product_id}
              </h1>
              <span
                style={{
                  fontSize: '7.5pt',
                  padding: '1px 6px',
                  border: `1px solid ${STATUS_COLORS[campaign.status] ?? 'rgba(255,255,255,0.2)'}`,
                  color: STATUS_COLORS[campaign.status] ?? 'var(--omega-ink-subtle)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  borderRadius: 3,
                }}
              >
                {campaign.status}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 14, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', flexWrap: 'wrap' }}>
              {campaign.slug && (
                <span style={{ color: '#7dd3fc' }}>/{campaign.slug}</span>
              )}
              <span>Created {fmtTime(campaign.created_at)}</span>
              {campaign.executed_at && (
                <span>Executed {fmtTime(campaign.executed_at)}</span>
              )}
              <span style={{ fontFamily: 'monospace', fontSize: '8pt' }}>
                product: {campaign.product_id.slice(0, 16)}…
              </span>
            </div>
          </div>

          {/* 4-Specialist panels */}
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: '8.5pt',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--omega-ink-subtle)',
                marginBottom: 12,
              }}
            >
              Specialist Outputs
            </div>
            <CampaignPanels content={content} />
          </div>

          {/* Action bar */}
          <ActionBar
            campaign={campaign}
            onAction={(action, extra) => actionMutation.mutate({ action, ...(extra !== undefined ? { extra } : {}) })}
            busy={actionMutation.isPending}
            error={actionError}
          />
        </>
      )}
    </div>
  );
}
